import React, { useState, useEffect } from "react";
import SEO from "../seo";
const GenerateYourLandingPage = () => {
  const [activeSection, setActiveSection] = useState("ai-powered");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["ai-powered", "preview-optimize"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Generate Your Landing Page – xPAGE Help Center"
        description="Learn how to generate a fully functional landing page with xPage. AI-powered layout creation, customizable blocks, and live preview to launch your store quickly."
        keywords="xPAGE, landing page builder, AI landing page, eCommerce store, xPage builder, online store"
        canonical="https://help.xpage.ai/help/quick-launch-checklist/Generate%20Your%20Landing%20Page"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Generate Your Landing Page
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Once your brand identity is set, it's time to build your landing page.
          xPage gives you the flexibility to create either a fully functional
          landing page. With AI handling the heavy lifting, your store can be up
          and running in minutes.
        </p>

        {/* AI-Powered Section */}
        <section id="ai-powered" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            AI-Powered Landing Page Generation
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">
            Once you select your format, xPage instantly generates your layout
            using smart defaults tailored to your product category and brand
            style. The AI will automatically include:
          </p>

          <ul className="list-disc list-inside space-y-1 mb-4">
            <li className="text-sm text-systemLabel">
              <span className="font-semibold">Banners</span> with your brand
              colors and logo
            </li>
            <li className="text-sm text-systemLabel">
              <span className="font-semibold">Product sections</span> with
              images, descriptions, and pricing
            </li>
            <li className="text-sm text-systemLabel">
              <span className="font-semibold">Built-in testimonials</span> and
              product guarantees
            </li>
            <li className="text-sm text-systemLabel">
              <span className="font-semibold">Scarcity blocks</span> like
              countdown timers or limited stock notices
            </li>
            <li className="text-sm text-systemLabel">
              <span className="font-semibold">Checkout blocks</span>, optimized
              for mobile devices
            </li>
          </ul>

          <p className="text-sm text-systemLabel">
            All of this happens within few minutes giving you a professionally
            designed layout that's ready to customize.
          </p>
        </section>

        {/* Preview and Optimize Section */}
        <section id="preview-optimize" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Preview and optimize
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">
            After your landing page is generated, you can view a live preview
            right from your dashboard. You can make edits using the xPage
            Builder, rearrange content blocks, change copy, or add upsells.
          </p>

          <p className="text-sm text-systemLabel">
            No coding is required. Just drag, drop, and customize with full
            control backed by AI suggestions if you need them.
          </p>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#ai-powered"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "ai-powered"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                AI-Powered Generation
              </a>
            </li>
            <li>
              <a
                href="#preview-optimize"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "preview-optimize"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Preview & Optimize
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GenerateYourLandingPage;
